import React, { useState } from 'react';
import { SearchIcon, DownloadIcon, FilterIcon, XIcon } from 'lucide-react';
export function TransactionSearch() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [senderBank, setSenderBank] = useState('');
  const [receiverBank, setReceiverBank] = useState('');
  const [senderAccount, setSenderAccount] = useState('');
  const [receiverAccount, setReceiverAccount] = useState('');
  const [status, setStatus] = useState<'all' | 'success' | 'failed'>('all');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const banks = [{
    code: '',
    name: 'Select Bank'
  }, {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  }, {
    code: 'AWINETAA',
    name: 'Awash Bank S.C.'
  }, {
    code: 'CBETETAA',
    name: 'Commercial Bank of Ethiopia (CBE)'
  }, {
    code: 'BUNAETAA',
    name: 'Bunna International Bank S.C.'
  }, {
    code: 'DASHETAA',
    name: 'Dashen Bank S.C.'
  }, {
    code: 'CBORETAA',
    name: 'Cooperative Bank of Oromia S.C. (COOP)'
  }, {
    code: 'ZEMEETAA',
    name: 'Zemen Bank S.C.'
  }, {
    code: 'NIBIETAA',
    name: 'NIB International Bank S.C. (NIB)'
  }, {
    code: 'HIJRETAA',
    name: 'Hibret Bank S.C.'
  }, {
    code: 'TSCPETAA',
    name: 'Tsehay Bank S.C.'
  }, {
    code: 'WEGAETAA',
    name: 'Wegagen Bank S.C.'
  }, {
    code: 'ABAYETAA',
    name: 'Abay Bank S.C.'
  }];
  // Sample data - in a real app this would come from an API
  const sampleTransactions = [{
    id: 1,
    dateTime: '2023-05-10 14:30:22',
    senderBankBIC: 'AMHRETAA',
    senderBankName: 'Amhara Bank S.C.',
    senderAccount: '1000123456789',
    receiverBankBIC: 'CBETETAA',
    receiverBankName: 'Commercial Bank of Ethiopia (CBE)',
    receiverAccount: '2000987654321',
    amount: 5000,
    purposeCode: 'P2P',
    status: 'Success',
    failureReason: '',
    narration: 'Payment for services'
  }, {
    id: 2,
    dateTime: '2023-05-09 10:28:15',
    senderBankBIC: 'AWINETAA',
    senderBankName: 'Awash Bank S.C.',
    senderAccount: '1000223456789',
    receiverBankBIC: 'DASHETAA',
    receiverBankName: 'Dashen Bank S.C.',
    receiverAccount: '2000887654321',
    amount: 3500,
    purposeCode: 'BILL',
    status: 'Failed',
    failureReason: 'Insufficient funds',
    narration: 'Utility payment'
  }, {
    id: 3,
    dateTime: '2023-05-08 16:25:47',
    senderBankBIC: 'BUNAETAA',
    senderBankName: 'Bunna International Bank S.C.',
    senderAccount: '1000323456789',
    receiverBankBIC: 'ZEMEETAA',
    receiverBankName: 'Zemen Bank S.C.',
    receiverAccount: '2000787654321',
    amount: 2800,
    purposeCode: 'P2P',
    status: 'Success',
    failureReason: '',
    narration: 'Loan repayment'
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate date range (in a real app, you'd check server-side too)
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffTime = Math.abs(to.getTime() - from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 90) {
        alert('Date range cannot exceed 90 days');
        return;
      }
      if (from > to) {
        alert('From date must be before To date');
        return;
      }
    }
    // Filter sample data based on search criteria
    let results = [...sampleTransactions];
    if (senderBank) {
      results = results.filter(t => t.senderBankBIC === senderBank);
    }
    if (receiverBank) {
      results = results.filter(t => t.receiverBankBIC === receiverBank);
    }
    if (senderAccount) {
      results = results.filter(t => t.senderAccount.includes(senderAccount));
    }
    if (receiverAccount) {
      results = results.filter(t => t.receiverAccount.includes(receiverAccount));
    }
    if (status !== 'all') {
      results = results.filter(t => status === 'success' && t.status === 'Success' || status === 'failed' && t.status === 'Failed');
    }
    setSearchResults(results);
    setHasSearched(true);
  };
  const handleRowClick = (transaction: any) => {
    setSelectedTransaction(transaction);
    setShowDetails(true);
  };
  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setSenderBank('');
    setReceiverBank('');
    setSenderAccount('');
    setReceiverAccount('');
    setStatus('all');
    setSearchResults([]);
    setHasSearched(false);
  };
  return <div className="space-y-6" data-id="element-1883">
      <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-1884">Transaction Search</h1>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-1885">
        <form onSubmit={handleSearch} className="space-y-6" data-id="element-1886">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1887">
            <div className="space-y-2" data-id="element-1888">
              <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700" data-id="element-1889">
                From Date
              </label>
              <input id="fromDate" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1890" />
            </div>
            <div className="space-y-2" data-id="element-1891">
              <label htmlFor="toDate" className="block text-sm font-medium text-gray-700" data-id="element-1892">
                To Date
              </label>
              <input id="toDate" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-1893" />
            </div>
            <div className="space-y-2" data-id="element-1894">
              <label htmlFor="senderBank" className="block text-sm font-medium text-gray-700" data-id="element-1895">
                Sender Bank
              </label>
              <select id="senderBank" value={senderBank} onChange={e => setSenderBank(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1896">
                {banks.map(bank => <option key={bank.code} value={bank.code} data-id="element-1897">
                    {bank.name}
                  </option>)}
              </select>
            </div>
            <div className="space-y-2" data-id="element-1898">
              <label htmlFor="receiverBank" className="block text-sm font-medium text-gray-700" data-id="element-1899">
                Receiver Bank
              </label>
              <select id="receiverBank" value={receiverBank} onChange={e => setReceiverBank(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-1900">
                {banks.map(bank => <option key={bank.code} value={bank.code} data-id="element-1901">
                    {bank.name}
                  </option>)}
              </select>
            </div>
            <div className="space-y-2" data-id="element-1902">
              <label htmlFor="senderAccount" className="block text-sm font-medium text-gray-700" data-id="element-1903">
                Sender Account
              </label>
              <input id="senderAccount" type="text" value={senderAccount} onChange={e => setSenderAccount(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter sender account" data-id="element-1904" />
            </div>
            <div className="space-y-2" data-id="element-1905">
              <label htmlFor="receiverAccount" className="block text-sm font-medium text-gray-700" data-id="element-1906">
                Receiver Account
              </label>
              <input id="receiverAccount" type="text" value={receiverAccount} onChange={e => setReceiverAccount(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" placeholder="Enter receiver account" data-id="element-1907" />
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-1908">
            <label className="text-sm font-medium text-gray-700 mr-3" data-id="element-1909">
              Status:
            </label>
            <div className="flex items-center gap-2" data-id="element-1910">
              <button type="button" onClick={() => setStatus('all')} className={`px-3 py-1 text-xs rounded-full ${status === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1911">
                All
              </button>
              <button type="button" onClick={() => setStatus('success')} className={`px-3 py-1 text-xs rounded-full ${status === 'success' ? 'bg-green-100 text-green-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1912">
                Success
              </button>
              <button type="button" onClick={() => setStatus('failed')} className={`px-3 py-1 text-xs rounded-full ${status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1913">
                Failed
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-1914">
            <button type="submit" className="px-4 py-2 bg-[#F58220] text-white rounded-md font-medium hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50" data-id="element-1915">
              <SearchIcon size={16} className="inline mr-2" data-id="element-1916" />
              Search
            </button>
            <button type="button" onClick={handleReset} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50" data-id="element-1917">
              Reset
            </button>
          </div>
        </form>
      </div>
      {hasSearched && <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1918">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1919">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-1920">Search Results</h3>
            {searchResults.length > 0 && <div className="flex items-center gap-3" data-id="element-1921">
                <button className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1922">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1923" />
                  Export CSV
                </button>
                <button className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1924">
                  <DownloadIcon size={16} className="mr-2" data-id="element-1925" />
                  Export PDF
                </button>
              </div>}
          </div>
          {searchResults.length === 0 ? <div className="p-8 text-center" data-id="element-1926">
              <p className="text-gray-500" data-id="element-1927">
                No transactions found matching your search criteria.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1928">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1929">
                <thead className="bg-gray-50" data-id="element-1930">
                  <tr data-id="element-1931">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1932">
                      Date Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1933">
                      Sender Bank
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1934">
                      Sender Account
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1935">
                      Receiver Bank
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1936">
                      Receiver Account
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1937">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1938">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1939">
                  {searchResults.map(transaction => <tr key={transaction.id} onClick={() => handleRowClick(transaction)} className={`cursor-pointer hover:bg-gray-50 ${transaction.status === 'Failed' ? 'bg-red-50' : ''}`} data-id="element-1940">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1941">
                        {transaction.dateTime}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1942">
                        {transaction.senderBankBIC}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1943">
                        {transaction.senderAccount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1944">
                        {transaction.receiverBankBIC}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1945">
                        {transaction.receiverAccount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1946">
                        ETB {transaction.amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1947">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1948">
                          {transaction.status}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}
      {showDetails && selectedTransaction && <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-10" data-id="element-1949">
          <div className="bg-white w-full max-w-md h-full overflow-auto" data-id="element-1950">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1951">
              <h3 className="font-medium text-[#1A1F36]" data-id="element-1952">
                Transaction Details
              </h3>
              <button onClick={() => setShowDetails(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1953">
                <XIcon size={20} data-id="element-1954" />
              </button>
            </div>
            <div className="p-4 space-y-4" data-id="element-1955">
              <div data-id="element-1956">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1957">
                  Status
                </h4>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedTransaction.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1958">
                  {selectedTransaction.status}
                </span>
                {selectedTransaction.failureReason && <p className="mt-1 text-sm text-red-600" data-id="element-1959">
                    {selectedTransaction.failureReason}
                  </p>}
              </div>
              <div data-id="element-1960">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1961">
                  Date & Time
                </h4>
                <p className="text-sm text-gray-900" data-id="element-1962">
                  {selectedTransaction.dateTime}
                </p>
              </div>
              <div data-id="element-1963">
                <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-1964">
                  Amount
                </h4>
                <p className="text-lg font-semibold text-[#1A1F36]" data-id="element-1965">
                  ETB {selectedTransaction.amount.toLocaleString()}
                </p>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1966">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1967">
                  Sender Details
                </h4>
                <div className="space-y-2" data-id="element-1968">
                  <div data-id="element-1969">
                    <p className="text-xs text-gray-500" data-id="element-1970">Bank BIC</p>
                    <p className="text-sm text-gray-900" data-id="element-1971">
                      {selectedTransaction.senderBankBIC}
                    </p>
                  </div>
                  <div data-id="element-1972">
                    <p className="text-xs text-gray-500" data-id="element-1973">Bank Name</p>
                    <p className="text-sm text-gray-900" data-id="element-1974">
                      {selectedTransaction.senderBankName}
                    </p>
                  </div>
                  <div data-id="element-1975">
                    <p className="text-xs text-gray-500" data-id="element-1976">Account</p>
                    <p className="text-sm text-gray-900" data-id="element-1977">
                      {selectedTransaction.senderAccount}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1978">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1979">
                  Receiver Details
                </h4>
                <div className="space-y-2" data-id="element-1980">
                  <div data-id="element-1981">
                    <p className="text-xs text-gray-500" data-id="element-1982">Bank BIC</p>
                    <p className="text-sm text-gray-900" data-id="element-1983">
                      {selectedTransaction.receiverBankBIC}
                    </p>
                  </div>
                  <div data-id="element-1984">
                    <p className="text-xs text-gray-500" data-id="element-1985">Bank Name</p>
                    <p className="text-sm text-gray-900" data-id="element-1986">
                      {selectedTransaction.receiverBankName}
                    </p>
                  </div>
                  <div data-id="element-1987">
                    <p className="text-xs text-gray-500" data-id="element-1988">Account</p>
                    <p className="text-sm text-gray-900" data-id="element-1989">
                      {selectedTransaction.receiverAccount}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-200" data-id="element-1990">
                <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-1991">
                  Additional Information
                </h4>
                <div className="space-y-2" data-id="element-1992">
                  <div data-id="element-1993">
                    <p className="text-xs text-gray-500" data-id="element-1994">Purpose Code</p>
                    <p className="text-sm text-gray-900" data-id="element-1995">
                      {selectedTransaction.purposeCode}
                    </p>
                  </div>
                  <div data-id="element-1996">
                    <p className="text-xs text-gray-500" data-id="element-1997">Narration</p>
                    <p className="text-sm text-gray-900" data-id="element-1998">
                      {selectedTransaction.narration}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}